import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ArrowLeft, Save, Loader2, ChevronLeft, ChevronRight, QrCode, Smartphone, Plus, Trash2, Download, MapPin
} from "lucide-react";
import { API } from "@/lib/api";
import { formatRewardSummary } from "@/lib/rewardDisplay";
import StampIconPicker from "@/components/StampIconPicker";
import { resolveStampIconId } from "@/lib/lucideStampIconIds";


const PASS_STYLES = [
  { id: "storeCard", label: "Store Card", images: ["logo", "icon", "strip"] },
  { id: "coupon", label: "Coupon", images: ["logo", "icon", "strip"] },
  { id: "eventTicket", label: "Event Ticket", images: ["logo", "icon", "strip", "thumbnail", "background"] },
  { id: "generic", label: "Generic", images: ["logo", "icon", "thumbnail"] },
  { id: "boardingPass", label: "Boarding Pass", images: ["logo", "icon", "footer"] },
];

const VALUE_OPTIONS = [
  { value: "first_name", label: "First name" },
  { value: "full_name", label: "Full name" },
  { value: "email", label: "Email" },
  { value: "phone", label: "Phone" },
  { value: "point_balance", label: "Point balance" },
  { value: "stamps", label: "Stamps (current/total)" },
  { value: "tier_name", label: "Tier name" },
  { value: "next_reward", label: "Next reward" },
  { value: "active_reward", label: "Active reward" },
  { value: "customer_id", label: "Customer ID" },
  { value: "member_since", label: "Member since" },
  { value: "custom_text", label: "Custom text" },
];

const EMPTY_FIELD = { visible: true, label: "", value: "", change_message: "" };
const EMPTY_LOCATION = {
  name: "",
  latitude: "",
  longitude: "",
  altitude: "",
  relevant_text: "",
};
const MAX_LOCATIONS = 10;
const MAX_FEATURED_ACTIONS = 2;

const FEATURED_ACTION_OPTIONS = [
  { value: "viewOffersRewards", label: "View Offers and Rewards", category: "Membership" },
  { value: "membershipBenefits", label: "View Membership Benefits", category: "Membership" },
  { value: "addToBalance", label: "Add to Balance", category: "Membership" },
  { value: "order", label: "Order Delivery or Pickup", category: "Membership" },
  { value: "shop", label: "Shop Online or In-App", category: "Membership" },
  { value: "bookAppointment", label: "Book an Appointment", category: "Membership" },
  { value: "bookCar", label: "Book a Car", category: "Membership" },
  { value: "bookFlight", label: "Book a Flight", category: "Membership" },
  { value: "bookStay", label: "Book a Stay", category: "Membership" },
  { value: "viewSchedule", label: "View Schedule", category: "Events" },
  { value: "watchTrailer", label: "Watch Trailer", category: "Events" },
  { value: "listenToMusic", label: "Listen to Music", category: "Events" },
  { value: "call", label: "Call", category: "Generic" },
  { value: "place", label: "Go to Location (Apple Maps)", category: "Generic" },
];

const EMPTY_FEATURED_ACTION = {
  identifier: "",
  type: "viewOffersRewards",
  url: "",
  place_identifier: "",
};

const DEFAULT_CONFIG = {
  pass_style: "storeCard",
  transit_type: "PKTransitTypeAir",
  colors: { background: "#4a2810", foreground: "#ffffff", label: "#c4a882" },
  title: { visible: true, text: "Loyalty Programme" },
  organization_name: "",
  strip_image: { visible: true },
  header_fields: [{ visible: true, label: "Hi", value: "first_name", change_message: "" }],
  primary_fields: [{ visible: true, label: "POINTS", value: "point_balance", change_message: "" }],
  secondary_fields: [],
  auxiliary_fields: [],
  back_fields: [],
  barcode: { type: "QR", alt_text: "" },
  locations: [],
  relevant_date: "",
  featured_actions: [],
  app_launch_url: "",
  sharing_prohibited: false,
  stamps: {
    icon: "star",
    stamped_color: "#ffffff",
    unstamped_color: "#555555",
    max_per_line: 5,
    background_color: "#4a2810",
  },
};

function sampleRewardLabel(vendor) {
  const sr = vendor?.stamp_rewards || [];
  if (sr.length > 0) {
    const sorted = [...sr].sort((a, b) => (a.orders_required || 0) - (b.orders_required || 0));
    const preview = formatRewardSummary(sorted[0]);
    return preview.short || preview.summary || "Reward";
  }
  const gbp = Number(vendor?.reward_value_gbp);
  if (gbp > 0) {
    return Number.isInteger(gbp) ? `£${gbp}` : `£${gbp.toFixed(2)}`;
  }
  return "Free coffee";
}

function getSample(key, vendor) {
  const ob = vendor?.reward_logic === "order_based";
  const sr = vendor?.stamp_rewards || [];
  const t = sr.length > 0 ? Math.max(...sr.map(s => s.orders_required || 0)) : 10;
  const s = Math.floor(t * 0.6);
  const rewardSample = sampleRewardLabel(vendor);
  const m = { first_name: "Saroj", full_name: "Saroj Kumar", email: "saroj@email.com", phone: "+44 7700 900",
    point_balance: ob ? String(s) : "350", stamps: `${s}/${t}`, tier_name: "Gold",
    next_reward: rewardSample, active_reward: rewardSample,
    customer_id: "123-456-789",
    member_since: "Jan 2025", custom_text: "Custom" };
  return m[key] || "";
}

/* ── Single Field Editor ── */
function FieldCard({ field, onChange, onRemove }) {
  const f = field || EMPTY_FIELD;
  return (
    <div className="rounded-lg border border-nory-purple/10 bg-nory-cream p-3 space-y-2.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Switch checked={f.visible !== false} onCheckedChange={(v) => onChange({ ...f, visible: v })} />
          <span className="text-[11px] text-nory-purple/40">{f.visible !== false ? "Visible" : "Hidden"}</span>
        </div>
        {onRemove && <button onClick={onRemove} className="text-nory-purple/40 hover:text-red-500 p-0.5"><Trash2 size={13} /></button>}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">Label</p>
          <Input value={f.label || ""} onChange={(e) => onChange({ ...f, label: e.target.value })} className="h-7 text-xs" placeholder="Label..." />
        </div>
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">Value</p>
          <Select value={f.value || "custom_text"} onValueChange={(v) => onChange({ ...f, value: v })}>
            <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{VALUE_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div>
        <p className="text-[10px] text-nory-purple/40 mb-0.5">Change message (lock screen notification)</p>
        <Input value={f.change_message || ""} onChange={(e) => onChange({ ...f, change_message: e.target.value })} className="h-7 text-xs" placeholder="e.g. Your balance is now %@" />
      </div>
    </div>
  );
}

/* ── Field List ── */
function FieldList({ fields, onChange, max }) {
  const add = () => { if (fields.length < max) onChange([...fields, { ...EMPTY_FIELD }]); };
  const update = (i, f) => { const u = [...fields]; u[i] = f; onChange(u); };
  const remove = (i) => onChange(fields.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {fields.map((f, i) => <FieldCard key={i} field={f} onChange={(v) => update(i, v)} onRemove={fields.length > 0 ? () => remove(i) : null} />)}
      {fields.length < max && (
        <button onClick={add} className="w-full h-8 rounded-lg border-2 border-dashed border-nory-purple/10 hover:border-nory-purple/30 text-nory-purple/40 hover:text-nory-purple/60 text-xs flex items-center justify-center gap-1 transition-colors">
          <Plus size={12} /> Add field
        </button>
      )}
    </div>
  );
}

/* ── Lock-screen location (PassKit relevance) ── */
function LocationCard({ location, onChange, onRemove }) {
  const loc = location || EMPTY_LOCATION;
  return (
    <div className="rounded-lg border border-nory-purple/10 bg-nory-cream p-3 space-y-2.5" data-testid="location-card">
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <MapPin size={13} className="text-nory-purple/40 shrink-0" />
          <Input
            value={loc.name || ""}
            onChange={(e) => onChange({ ...loc, name: e.target.value })}
            className="h-7 text-xs"
            placeholder="Location name (optional)"
            data-testid="location-name"
          />
        </div>
        {onRemove && (
          <button onClick={onRemove} className="text-nory-purple/40 hover:text-red-500 p-0.5 shrink-0" data-testid="remove-location">
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 gap-2">
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">Latitude</p>
          <Input
            type="number"
            step="any"
            value={loc.latitude ?? ""}
            onChange={(e) => onChange({ ...loc, latitude: e.target.value })}
            className="h-7 text-xs font-mono"
            placeholder="51.5074"
            data-testid="location-latitude"
          />
        </div>
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">Longitude</p>
          <Input
            type="number"
            step="any"
            value={loc.longitude ?? ""}
            onChange={(e) => onChange({ ...loc, longitude: e.target.value })}
            className="h-7 text-xs font-mono"
            placeholder="-0.1278"
            data-testid="location-longitude"
          />
        </div>
      </div>
      <div>
        <p className="text-[10px] text-nory-purple/40 mb-0.5">Altitude (optional, meters)</p>
        <Input
          type="number"
          step="any"
          value={loc.altitude ?? ""}
          onChange={(e) => onChange({ ...loc, altitude: e.target.value })}
          className="h-7 text-xs font-mono"
          placeholder="Optional"
          data-testid="location-altitude"
        />
      </div>
      <div>
        <p className="text-[10px] text-nory-purple/40 mb-0.5">Lock screen text</p>
        <Input
          value={loc.relevant_text || ""}
          onChange={(e) => onChange({ ...loc, relevant_text: e.target.value })}
          className="h-7 text-xs"
          placeholder="e.g. You're near our store — show your card"
          data-testid="location-relevant-text"
        />
      </div>
    </div>
  );
}

function LocationList({ locations, onChange }) {
  const list = Array.isArray(locations) ? locations : [];
  const add = () => {
    if (list.length < MAX_LOCATIONS) onChange([...list, { ...EMPTY_LOCATION }]);
  };
  const update = (i, loc) => {
    const next = [...list];
    next[i] = loc;
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {list.map((loc, i) => (
        <LocationCard
          key={i}
          location={loc}
          onChange={(v) => update(i, v)}
          onRemove={() => remove(i)}
        />
      ))}
      {list.length < MAX_LOCATIONS && (
        <button
          onClick={add}
          className="w-full h-8 rounded-lg border-2 border-dashed border-nory-purple/10 hover:border-nory-purple/30 text-nory-purple/40 hover:text-nory-purple/60 text-xs flex items-center justify-center gap-1 transition-colors"
          data-testid="add-location"
        >
          <Plus size={12} /> Add location
        </button>
      )}
    </div>
  );
}

/* ── Featured Actions (iOS 27 tiles; Google links module) ── */
function FeaturedActionCard({ action, onChange, onRemove }) {
  const a = action || EMPTY_FEATURED_ACTION;
  const isPlace = a.type === "place";
  const isCall = a.type === "call";
  return (
    <div className="rounded-lg border border-nory-purple/10 bg-nory-cream p-3 space-y-2.5" data-testid="featured-action-card">
      <div className="flex items-center justify-between gap-2">
        <Select value={a.type || "viewOffersRewards"} onValueChange={(v) => onChange({ ...a, type: v })}>
          <SelectTrigger className="h-7 text-xs flex-1" data-testid="featured-action-type">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {FEATURED_ACTION_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        {onRemove && (
          <button onClick={onRemove} className="text-nory-purple/40 hover:text-red-500 p-0.5 shrink-0" data-testid="remove-featured-action">
            <Trash2 size={13} />
          </button>
        )}
      </div>
      <div>
        <p className="text-[10px] text-nory-purple/40 mb-0.5">Identifier (stable id)</p>
        <Input
          value={a.identifier || ""}
          onChange={(e) => onChange({ ...a, identifier: e.target.value })}
          className="h-7 text-xs font-mono"
          placeholder="e.g. offers"
          data-testid="featured-action-id"
        />
      </div>
      {isPlace ? (
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">Apple Maps Place ID</p>
          <Input
            value={a.place_identifier || ""}
            onChange={(e) => onChange({ ...a, place_identifier: e.target.value })}
            className="h-7 text-xs font-mono"
            placeholder="e.g. I7C250D2CDCB364A"
            data-testid="featured-action-place-id"
          />
        </div>
      ) : (
        <div>
          <p className="text-[10px] text-nory-purple/40 mb-0.5">{isCall ? "Phone (tel:)" : "URL"}</p>
          <Input
            value={a.url || ""}
            onChange={(e) => onChange({ ...a, url: e.target.value })}
            className="h-7 text-xs"
            placeholder={isCall ? "tel:+447700900123" : "https://…"}
            data-testid="featured-action-url"
          />
        </div>
      )}
    </div>
  );
}

function FeaturedActionList({ actions, onChange }) {
  const list = Array.isArray(actions) ? actions : [];
  const add = () => {
    if (list.length < MAX_FEATURED_ACTIONS) {
      onChange([...list, { ...EMPTY_FEATURED_ACTION, identifier: `action-${list.length + 1}` }]);
    }
  };
  const update = (i, action) => {
    const next = [...list];
    next[i] = action;
    onChange(next);
  };
  const remove = (i) => onChange(list.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-2">
      {list.map((action, i) => (
        <FeaturedActionCard
          key={i}
          action={action}
          onChange={(v) => update(i, v)}
          onRemove={() => remove(i)}
        />
      ))}
      {list.length < MAX_FEATURED_ACTIONS && (
        <button
          onClick={add}
          className="w-full h-8 rounded-lg border-2 border-dashed border-nory-purple/10 hover:border-nory-purple/30 text-nory-purple/40 hover:text-nory-purple/60 text-xs flex items-center justify-center gap-1 transition-colors"
          data-testid="add-featured-action"
        >
          <Plus size={12} /> Add featured action
        </button>
      )}
    </div>
  );
}

/* ── PREVIEW ── */
function Preview({ config, vendor }) {
  const bg = config.colors?.background || "#4a2810";
  const fg = config.colors?.foreground || "#ffffff";
  const lbl = config.colors?.label || (fg + "99");
  const ob = vendor?.reward_logic === "order_based";
  const sr = vendor?.stamp_rewards || [];
  const target = ob && sr.length > 0 ? Math.max(...sr.map(s => s.orders_required || 0)) : (vendor?.orders_per_reward || 10);
  const filled = Math.floor(target * 0.6);
  const hdr = (config.header_fields || []).filter(f => f.visible !== false);
  const pri = (config.primary_fields || []).filter(f => f.visible !== false);
  const sec = (config.secondary_fields || []).filter(f => f.visible !== false);
  const aux = (config.auxiliary_fields || []).filter(f => f.visible !== false);

  const Row = ({ fields, size }) => {
    if (!fields.length) return null;
    return (
      <div className="flex gap-2 px-4 py-1">
        {fields.map((f, i) => (
          <div key={i} className="flex-1 min-w-0">
            {f.label && <p className="text-[9px] uppercase tracking-wider font-medium truncate" style={{ color: lbl }}>{f.label}</p>}
            <p className={`font-bold truncate ${size === "lg" ? "text-xl" : "text-sm"}`} style={{ color: fg }}>{getSample(f.value, vendor)}</p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center" data-testid="wallet-preview">
      <div className="w-[330px] rounded-2xl overflow-hidden shadow-2xl" style={{ backgroundColor: bg }} data-testid="wallet-card">
        {/* Header: Icon + Title + Header fields */}
        <div className="px-4 pt-3 pb-1 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {vendor?.logo_icon_path ? (
              <img src={`${API}/vendors/${vendor.id}/logo/icon`} alt="" className="w-9 h-9 rounded-lg object-cover" />
            ) : (
              <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: fg + "22" }}>
                <Smartphone size={15} style={{ color: fg }} />
              </div>
            )}
            {config.title?.visible !== false && (
              <span className="text-[13px] font-bold" style={{ color: fg }}>{config.title?.text || vendor?.name || "Pass"}</span>
            )}
          </div>
          {hdr.length > 0 && (
            <div className="flex gap-2">
              {hdr.map((f, i) => (
                <div key={i} className="text-right">
                  {f.label && <p className="text-[8px] uppercase tracking-wider" style={{ color: lbl }}>{f.label}</p>}
                  <p className="text-base font-bold" style={{ color: fg }}>{getSample(f.value, vendor)}</p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Stamp strip — primary text is baked into strip.png; primaryFields stay empty in pass.json */}
        {ob && config.pass_style === "storeCard" && config.strip_image?.visible !== false && (() => {
          const sc = config.stamps || {};
          const sectionBg = sc.background_color || bg || "#5a3825";
          const primaryField = pri[0];
          const params = new URLSearchParams({
            target: String(target), current: String(filled),
            icon: resolveStampIconId(sc.icon || "star"),
            sc: (sc.stamped_color || "#ffffff").replace("#", ""),
            uc: (sc.unstamped_color || "#000000").replace("#", ""),
            bg: sectionBg.replace("#", ""),
            ic: (bg || "#5a3825").replace("#", ""),
            vendor_id: vendor?.id || "",
            banner: sc.use_banner_bg ? "1" : "0",
            style: sc.style || "circles",
            max_per_line: String(sc.max_per_line || 5),
            top_reserve: primaryField ? "0.34" : "0",
            overlay_label: primaryField?.label || "",
            overlay_value: primaryField ? getSample(primaryField.value, vendor) : "",
            _v: String(sc._v || 0),
          });
          return (
            <div className="w-full relative" style={{ backgroundColor: sectionBg }} data-testid="stamp-section">
              <img src={`${API}/portal/stamp-strip.png?${params.toString()}`} alt="stamps"
                className="w-full block" data-testid="stamp-strip-preview" />
            </div>
          );
        })()}

        {/* Non-stamp strip — Apple overlays primary fields on the strip */}
        {!(ob && config.pass_style === "storeCard") && ["storeCard", "coupon", "eventTicket"].includes(config.pass_style) && config.strip_image?.visible !== false && (
          <div className="w-full relative" data-testid="strip-image-preview">
            {(vendor?.logo_strip_path || vendor?.logo_header_path) ? (
              <img
                src={`${API}/vendors/${vendor.id}/logo/${vendor.logo_strip_path ? "strip" : "header"}?_v=${config.strip_image?._v || 0}`}
                alt=""
                className="w-full block object-cover"
                style={{ aspectRatio: config.pass_style === "eventTicket" ? "375 / 98" : "375 / 123" }}
              />
            ) : (
              <div className="w-full flex items-center justify-center" style={{ backgroundColor: fg + "0d", aspectRatio: config.pass_style === "eventTicket" ? "375 / 98" : "375 / 123" }} data-testid="strip-image-placeholder">
                <p className="text-[10px]" style={{ color: fg + "55" }}>Strip image · {config.pass_style === "eventTicket" ? "375×98" : "375×123"}pt</p>
              </div>
            )}
            {pri.length > 0 && (
              <div className="absolute inset-x-0 bottom-0 pb-2" data-testid="primary-on-strip">
                <Row fields={pri} size="lg" />
              </div>
            )}
          </div>
        )}

        {/* Primary only when there is no strip band to overlay onto */}
        {!(["storeCard", "coupon", "eventTicket"].includes(config.pass_style) && config.strip_image?.visible !== false) && (
          <Row fields={pri} size="lg" />
        )}
        {/* Store cards: secondary + auxiliary share one PassKit row beneath the strip */}
        <Row fields={[...sec, ...aux]} size="sm" />

        {/* Barcode */}
        <div className="px-5 py-3 flex flex-col items-center">
          <div className="w-24 h-24 rounded-lg flex items-center justify-center bg-white">
            <QrCode size={56} className="text-nory-purple" />
          </div>
          <p className="text-[10px] mt-1 font-mono" style={{ color: fg + "66" }}>{config.barcode?.alt_text || getSample("first_name", vendor)}</p>
        </div>
      </div>
      <p className="text-[10px] text-nory-purple/40 mt-2">{PASS_STYLES.find(s => s.id === config.pass_style)?.label || "Store Card"}</p>
    </div>
  );
}

/* ── Back Preview ── */
function BackPreview({ config, vendor }) {
  const bg = config.colors?.background || "#4a2810";
  const fg = config.colors?.foreground || "#ffffff";
  const backs = (config.back_fields || []).filter(f => f.visible !== false);
  return (
    <div className="flex flex-col items-center" data-testid="details-preview">
      <div className="w-[330px] rounded-2xl overflow-hidden shadow-2xl p-5 space-y-3" style={{ backgroundColor: bg }}>
        {backs.length > 0 ? backs.map((f, i) => (
          <div key={i}>
            {f.label && <p className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: fg + "77" }}>{f.label}</p>}
            <p className="text-xs" style={{ color: fg + "bb" }}>{getSample(f.value, vendor) || "..."}</p>
            {i < backs.length - 1 && <div className="border-t mt-3" style={{ borderColor: fg + "1a" }} />}
          </div>
        )) : (
          <p className="text-xs text-center py-6" style={{ color: fg + "55" }}>Back fields auto-generate from vendor settings. Add custom ones in the edit panel.</p>
        )}
      </div>
    </div>
  );
}

/* ── Test Pass QR ── */
function TestPassQR({ vendorId, authHeaders }) {
  const [customers, setCustomers] = React.useState([]);
  const [selected, setSelected] = React.useState("");
  const [loaded, setLoaded] = React.useState(false);
  const [googleUrl, setGoogleUrl] = React.useState("");
  const [googleLoading, setGoogleLoading] = React.useState(false);

  React.useEffect(() => {
    if (loaded) return;
    axios.get(`${API}/customers?client_id=${vendorId}&limit=50`, { headers: authHeaders() })
      .then(({ data }) => {
        const list = data.customers || [];
        setCustomers(list);
        if (list.length > 0) setSelected(list[0].id);
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [vendorId, loaded, authHeaders]);

  const handleGooglePass = async () => {
    if (!selected) return;
    setGoogleLoading(true);
    setGoogleUrl("");
    try {
      const { data } = await axios.post(`${API}/portal/wallet-pass`, {
        platform: "google", customer_id: selected, client_id: vendorId,
      });
      if (data.save_url) setGoogleUrl(data.save_url);
    } catch (err) {
      console.error(err);
    } finally { setGoogleLoading(false); }
  };

  if (!loaded) return null;

  const passUrl = selected ? `${API}/portal/wallet-pass/apple.pkpass?customer_id=${selected}&client_id=${vendorId}` : "";
  const qrSrc = passUrl ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=8&data=${encodeURIComponent(passUrl)}` : "";

  return (
    <div className="mt-6 w-[330px] bg-white border border-nory-purple/10 rounded-2xl p-4 shadow-sm text-center" data-testid="test-pass-qr">
      <div className="flex items-center justify-center gap-2 mb-3">
        <Download size={14} className="text-nory-purple/50" />
        <p className="text-sm font-semibold text-nory-purple">Test Pass</p>
      </div>
      {customers.length === 0 ? (
        <p className="text-xs text-nory-purple/40 py-4">No customers yet. Add one via API to test.</p>
      ) : (
        <>
          <div className="mb-3">
            <Select value={selected} onValueChange={(v) => { setSelected(v); setGoogleUrl(""); }}>
              <SelectTrigger className="h-8 text-xs" data-testid="test-pass-customer-select"><SelectValue placeholder="Pick a customer..." /></SelectTrigger>
              <SelectContent>
                {customers.map(c => (
                  <SelectItem key={c.id} value={c.id}>{c.name || c.email || c.mobile_number || c.id.slice(0, 10)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {qrSrc && (
            <>
              <img src={qrSrc} alt="Scan to add pass" className="w-32 h-32 mx-auto rounded-xl border border-nory-purple/10" data-testid="test-pass-qr-img" />
              <p className="text-[10px] text-nory-purple/30 mt-2 mb-2">Scan with iPhone to add to Apple Wallet</p>
              <div className="flex items-center justify-center gap-2 flex-wrap">
                <a href={passUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-nory-purple text-white text-xs font-medium hover:bg-nory-maroon"
                  data-testid="test-pass-download-link">
                  <Download size={11} /> Apple .pkpass
                </a>
                <button onClick={handleGooglePass} disabled={googleLoading}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-nory-lilac/20 text-nory-purple text-xs font-medium hover:bg-nory-lilac/30 disabled:opacity-50"
                  data-testid="test-google-wallet-btn">
                  {googleLoading ? <Loader2 size={11} className="animate-spin" /> : <QrCode size={11} />} Google Wallet
                </button>
              </div>
              {googleUrl && (
                <a href={googleUrl} target="_blank" rel="noopener noreferrer"
                  className="block mt-2 text-[10px] text-nory-purple underline hover:text-nory-maroon" data-testid="google-wallet-link">
                  Open Google Wallet save link
                </a>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ═════════ MAIN ═════════ */
export default function WalletPassDesignerPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { authHeaders, user, loading: authLoading } = useAuth();
  const [vendor, setVendor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [page, setPage] = useState("front");
  const [leftOpen, setLeftOpen] = useState(true);
  const [rightOpen, setRightOpen] = useState(true);
  const [tab, setTab] = useState("design");

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/vendors/${id}`, { headers: authHeaders() });
      setVendor(data);
      const saved = data.wallet_pass_config;
      if (saved) {
        setConfig(prev => {
          const m = { ...prev };
          // Scalars
          if (saved.pass_style) m.pass_style = saved.pass_style;
          if (saved.transit_type) m.transit_type = saved.transit_type;
          if (saved.colors) m.colors = { ...prev.colors, ...saved.colors };
          if (saved.title) m.title = { ...prev.title, ...saved.title };
          if (saved.barcode) m.barcode = { ...prev.barcode, ...saved.barcode };
          if (saved.stamps) m.stamps = { ...prev.stamps, ...saved.stamps };
          if (saved.strip_image) m.strip_image = saved.strip_image;
          else if (saved.cover_image) m.strip_image = saved.cover_image;

          // Field arrays — migrate from old single-field format if needed
          if (Array.isArray(saved.header_fields)) {
            m.header_fields = saved.header_fields;
          } else if (saved.header_field && saved.header_field.visible !== false) {
            m.header_fields = [{ ...saved.header_field }];
          }

          if (Array.isArray(saved.primary_fields)) {
            m.primary_fields = saved.primary_fields;
          } else if (saved.primary_field && saved.primary_field.visible !== false) {
            m.primary_fields = [{ ...saved.primary_field }];
          }

          if (Array.isArray(saved.secondary_fields)) {
            m.secondary_fields = saved.secondary_fields;
          } else {
            const sf = [];
            if (saved.secondary_left && saved.secondary_left.visible) sf.push({ ...saved.secondary_left });
            if (saved.secondary_right && saved.secondary_right.visible) sf.push({ ...saved.secondary_right });
            if (sf.length > 0) m.secondary_fields = sf;
          }

          if (Array.isArray(saved.auxiliary_fields)) m.auxiliary_fields = saved.auxiliary_fields;
          if (Array.isArray(saved.back_fields)) m.back_fields = saved.back_fields;
          if (Array.isArray(saved.locations)) m.locations = saved.locations;
          if (saved.relevant_date != null) m.relevant_date = saved.relevant_date;
          else if (saved.relevantDate != null) m.relevant_date = saved.relevantDate;
          if (Array.isArray(saved.featured_actions)) m.featured_actions = saved.featured_actions;
          else if (Array.isArray(saved.featuredActions)) m.featured_actions = saved.featuredActions;
          if (saved.organization_name != null) m.organization_name = saved.organization_name;
          else if (saved.organizationName != null) m.organization_name = saved.organizationName;
          if (saved.app_launch_url != null) m.app_launch_url = saved.app_launch_url;
          else if (saved.appLaunchURL != null) m.app_launch_url = saved.appLaunchURL;
          if (saved.sharing_prohibited != null) m.sharing_prohibited = !!saved.sharing_prohibited;
          else if (saved.sharingProhibited != null) m.sharing_prohibited = !!saved.sharingProhibited;

          return m;
        });
      } else {
        setConfig(prev => ({
          ...prev,
          colors: { background: data.brand_color || prev.colors.background, foreground: data.text_color || prev.colors.foreground, label: data.body_text_color || prev.colors.label },
          title: { ...prev.title, text: data.name || prev.title.text },
        }));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [id, authHeaders]);

  useEffect(() => { load(); }, [load]);

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-nory-cream"><Loader2 size={24} className="animate-spin text-nory-purple/40" /></div>;
  if (!user) { navigate("/login", { replace: true }); return null; }

  const set = (path, value) => {
    setConfig(prev => {
      const c = JSON.parse(JSON.stringify(prev));
      const p = path.split(".");
      let r = c;
      for (let i = 0; i < p.length - 1; i++) { if (!r[p[i]]) r[p[i]] = {}; r = r[p[i]]; }
      r[p[p.length - 1]] = value;
      return c;
    });
  };

  const uploadStampAsset = async (assetType, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/vendors/${id}/upload-logo?logo_type=${assetType}`, fd, { headers: authHeaders() });
      set("stamps._v", Date.now());  // bust the preview image cache
      await load();
    } catch (err) { console.error(err); }
  };

  const uploadStripImage = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${API}/vendors/${id}/upload-logo?logo_type=strip`, fd, { headers: authHeaders() });
      set("strip_image._v", Date.now());
      await load();
    } catch (err) { console.error(err); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/vendors/${id}`, {
        wallet_pass_config: config,
        brand_color: config.colors.background,
        text_color: config.colors.foreground,
        body_text_color: config.colors.label,
      }, { headers: authHeaders() });
      await load();
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-nory-cream"><Loader2 size={24} className="animate-spin text-nory-purple/40" /></div>;

  const style = config.pass_style || "storeCard";
  const styleMeta = PASS_STYLES.find(s => s.id === style) || PASS_STYLES[0];
  const ob = vendor?.reward_logic === "order_based";
  const maxPri = style === "boardingPass" ? 2 : 1;
  const maxSec = 4;
  const maxAux = style === "boardingPass" ? 5 : 4;

  return (
    <div className="min-h-screen flex flex-col bg-nory-cream" data-testid="wallet-designer-page">
      {/* Top bar */}
      <div className="h-12 bg-[#1e1f4b] flex items-center justify-between px-4 shrink-0">
        <button onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-white/80 hover:text-white text-sm" data-testid="designer-back-btn">
          <ArrowLeft size={15} /><span>Back</span>
        </button>
        <h1 className="text-white text-sm font-semibold">Wallet Pass | {vendor?.name}</h1>
        <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#4f46e5] hover:bg-[#4338ca] text-white text-xs h-8 gap-1.5" data-testid="save-designer-btn">
          {saving ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />} Save
        </Button>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-nory-purple/10 flex justify-center gap-6 h-10 shrink-0">
        {["design", "notifications"].map(t => (
          <button key={t} onClick={() => setTab(t)} className={`text-sm font-medium px-1 border-b-2 capitalize ${tab === t ? "border-[#4f46e5] text-[#4f46e5]" : "border-transparent text-nory-purple/40 hover:text-nory-purple/60"}`} data-testid={`designer-tab-${t}`}>{t}</button>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left: Pages */}
        {leftOpen ? (
          <div className="w-40 bg-white border-r border-nory-purple/10 shrink-0" data-testid="pages-panel">
            <div className="px-3 pt-3 pb-1 flex items-center justify-between">
              <span className="text-xs font-semibold text-nory-purple/80">Pages</span>
              <button onClick={() => setLeftOpen(false)} className="text-nory-purple/40 hover:text-nory-purple/60"><ChevronLeft size={14} /></button>
            </div>
            {["front", "details"].map(p => (
              <button key={p} onClick={() => setPage(p)} className={`mx-2 mb-0.5 w-[calc(100%-16px)] text-left px-3 py-1.5 rounded text-sm capitalize ${page === p ? "bg-[#eef2ff] text-[#4f46e5] font-medium" : "text-nory-purple/50 hover:bg-nory-cream"}`} data-testid={`page-${p}-btn`}>{p}</button>
            ))}
          </div>
        ) : (
          <button onClick={() => setLeftOpen(true)} className="w-7 bg-white border-r border-nory-purple/10 flex items-center justify-center text-nory-purple/40 hover:text-nory-purple/60 shrink-0"><ChevronRight size={14} /></button>
        )}

        {/* Center: Preview */}
        <div className="flex-1 flex flex-col items-center overflow-y-auto py-6 px-4" data-testid="preview-panel">
          <div className="flex bg-white border border-nory-purple/10 rounded-lg overflow-hidden mb-6 shadow-sm">
            {["apple", "google"].map(p => (
              <button key={p} onClick={() => {}} className={`px-5 py-1.5 text-sm font-medium capitalize ${p === "apple" ? "bg-nory-purple/5 text-zinc-900" : "text-nory-purple/40"}`} data-testid={`platform-${p}-btn`}>{p}</button>
            ))}
          </div>
          {tab === "design" && page === "front" && <Preview config={config} vendor={vendor} />}
          {tab === "design" && page === "details" && <BackPreview config={config} vendor={vendor} />}
          {tab === "design" && page === "front" && <TestPassQR vendorId={id} authHeaders={authHeaders} />}
          {tab === "notifications" && (
            <div className="max-w-md bg-white border border-nory-purple/10 rounded-xl p-6 shadow-sm space-y-4">
              <div>
                <p className="text-sm font-medium text-nory-purple/70 mb-1">Field change messages</p>
                <p className="text-xs text-nory-purple/40 leading-relaxed">
                  Set a change message on each field in the Design tab. When that value updates, Apple Wallet can show it on the lock screen.
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-nory-purple/70 mb-1">Nearby places</p>
                <p className="text-xs text-nory-purple/40 leading-relaxed">
                  Latitude / longitude + lock screen text surface the pass near your stores (Apple) and via merchant locations (Google).
                </p>
                <p className="text-[11px] text-nory-purple/50 mt-2">
                  {(config.locations || []).length} location{(config.locations || []).length === 1 ? "" : "s"}
                  {config.relevant_date ? " · relevant time set" : ""}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-nory-purple/70 mb-1">Featured actions</p>
                <p className="text-xs text-nory-purple/40 leading-relaxed">
                  Up to two Apple Wallet tiles (iOS 27+). The same URLs appear as Google Wallet detail links.
                </p>
                <p className="text-[11px] text-nory-purple/50 mt-2">
                  {(config.featured_actions || []).length} action{(config.featured_actions || []).length === 1 ? "" : "s"} configured
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Right: Edit */}
        {rightOpen ? (
          <div className="w-[300px] bg-white border-l border-nory-purple/10 shrink-0 flex flex-col overflow-y-auto" data-testid="edit-panel">
            <div className="px-3 pt-3 pb-2 flex items-center justify-between border-b border-nory-purple/5 shrink-0">
              <span className="text-xs font-semibold text-nory-purple/80">Edit</span>
              <button onClick={() => setRightOpen(false)} className="text-nory-purple/40 hover:text-nory-purple/60"><ChevronRight size={14} /></button>
            </div>

            {tab === "design" && page === "front" && (
              <div className="flex-1 overflow-y-auto">
                <Accordion type="single" collapsible defaultValue="header_fields" className="px-3">

                  {/* Pass Style */}
                  <AccordionItem value="pass_style">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-pass-style-trigger">Pass style</AccordionTrigger>
                    <AccordionContent>
                      <Select value={style} onValueChange={(v) => set("pass_style", v)}>
                        <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{PASS_STYLES.map(s => <SelectItem key={s.id} value={s.id}>{s.label}</SelectItem>)}</SelectContent>
                      </Select>
                      {style === "boardingPass" && (
                        <div className="mt-2">
                          <p className="text-[10px] text-nory-purple/40 mb-1">Transit type</p>
                          <Select value={config.transit_type || "PKTransitTypeAir"} onValueChange={(v) => set("transit_type", v)}>
                            <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              {["Air", "Train", "Bus", "Boat", "Generic"].map(t => <SelectItem key={t} value={`PKTransitType${t}`}>{t}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                    </AccordionContent>
                  </AccordionItem>

                  {/* Colors */}
                  <AccordionItem value="colors">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-color-trigger">Colors</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {[{ k: "background", l: "Background" }, { k: "foreground", l: "Foreground" }, { k: "label", l: "Label" }].map(c => (
                          <div key={c.k} className="flex items-center gap-2">
                            <input type="color" value={config.colors?.[c.k] || "#000"} onChange={(e) => set(`colors.${c.k}`, e.target.value)} className="w-6 h-6 rounded border border-nory-purple/10 cursor-pointer p-0.5 shrink-0" />
                            <div className="flex-1">
                              <p className="text-[10px] text-nory-purple/50">{c.l}</p>
                              <Input value={config.colors?.[c.k] || ""} onChange={(e) => set(`colors.${c.k}`, e.target.value)} className="h-6 text-[10px] font-mono" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Images & Logo */}
                  <AccordionItem value="images">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-images-trigger">Images & Logo</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2.5">
                        <div>
                          <p className="text-[10px] text-nory-purple/40 mb-1">Logo text</p>
                          <Input value={config.title?.text || ""} onChange={(e) => set("title.text", e.target.value)} className="h-7 text-xs" placeholder="e.g. Loyalty Programme" />
                        </div>
                        <div>
                          <p className="text-[10px] text-nory-purple/40 mb-1">Issuer name (organizationName)</p>
                          <Input
                            value={config.organization_name || ""}
                            onChange={(e) => set("organization_name", e.target.value)}
                            className="h-7 text-xs"
                            placeholder={vendor?.name || "Defaults to vendor name"}
                            maxLength={64}
                            data-testid="organization-name"
                          />
                          <p className="text-[9px] text-nory-purple/35 mt-1">Lock-screen update title / share sheet. Max 64 chars.</p>
                        </div>
                        {styleMeta.images.includes("strip") && (
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <p className="text-xs text-nory-purple/60">Strip image</p>
                              <Switch checked={config.strip_image?.visible !== false} onCheckedChange={(v) => set("strip_image.visible", v)} />
                            </div>
                            {!(ob && style === "storeCard") && (
                              <div className="space-y-1.5">
                                <p className="text-[10px] text-nory-purple/40 leading-relaxed">
                                  Upload a full-width strip for non-stamp passes (Apple size <span className="font-mono">375×98pt</span>).
                                </p>
                                <div className="flex items-center gap-2">
                                  {(vendor?.logo_strip_path || vendor?.logo_header_path) ? (
                                    <img
                                      src={`${API}/vendors/${vendor.id}/logo/${vendor.logo_strip_path ? "strip" : "header"}?_v=${config.strip_image?._v || 0}`}
                                      alt=""
                                      className="h-9 w-16 rounded object-cover bg-nory-purple/5 border border-nory-purple/10 shrink-0"
                                    />
                                  ) : (
                                    <div className="h-9 w-16 rounded bg-nory-purple/5 border border-nory-purple/10 shrink-0" />
                                  )}
                                  <label className="flex-1 cursor-pointer">
                                    <span className="block text-[11px] font-semibold text-[#4f46e5]">Upload strip image</span>
                                    <span className="block text-[9px] text-nory-purple/40">PNG/JPEG · recommended 750×196px</span>
                                    <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStripImage(e.target.files[0])} data-testid="upload-strip-image" />
                                  </label>
                                </div>
                              </div>
                            )}
                            {ob && style === "storeCard" && (
                              <p className="text-[10px] text-nory-purple/40">Stamp cards use the Stamp style strip instead of a static strip image.</p>
                            )}
                          </div>
                        )}
                        <p className="text-[10px] text-nory-purple/40">Also supports: {styleMeta.images.filter((i) => i !== "strip").join(", ") || "logo"}.</p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Stamp Icons */}
                  {ob && style === "storeCard" && (
                    <AccordionItem value="stamps">
                      <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-stamps-trigger">Stamp style</AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2.5">
                          {/* Style selector */}
                          <div className="grid grid-cols-2 gap-1.5">
                            {[{ id: "circles", l: "Circles" }, { id: "image", l: "Image stamps" }].map(o => (
                              <button key={o.id} onClick={() => set("stamps.style", o.id)}
                                className={`py-1.5 rounded-lg text-[11px] font-semibold border-2 ${(config.stamps?.style || "circles") === o.id ? "border-[#4f46e5] bg-[#eef2ff] text-[#4f46e5]" : "border-nory-purple/10 text-nory-purple/50 hover:border-nory-purple/20"}`}
                                data-testid={`stamp-style-${o.id}`}>{o.l}</button>
                            ))}
                          </div>

                          <div>
                            <p className="text-[10px] text-nory-purple/50 mb-0.5">Max stamps per line</p>
                            <Select
                              value={String(config.stamps?.max_per_line || 5)}
                              onValueChange={(v) => set("stamps.max_per_line", Number(v))}
                            >
                              <SelectTrigger className="h-7 text-xs" data-testid="stamps-max-per-line">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                                  <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          <div className="flex items-center gap-2">
                            <input
                              type="color"
                              value={config.stamps?.background_color || config.colors?.background || "#4a2810"}
                              onChange={(e) => set("stamps.background_color", e.target.value)}
                              className="w-6 h-6 rounded border border-nory-purple/10 cursor-pointer p-0.5 shrink-0"
                              data-testid="stamps-background-color"
                            />
                            <div className="flex-1">
                              <p className="text-[10px] text-nory-purple/50">Section background</p>
                              <Input
                                value={config.stamps?.background_color || config.colors?.background || "#4a2810"}
                                onChange={(e) => set("stamps.background_color", e.target.value)}
                                className="h-6 text-[10px] font-mono"
                              />
                            </div>
                          </div>

                          {(config.stamps?.style || "circles") === "image" ? (
                            <div className="space-y-2 pt-1">
                              <p className="text-[10px] text-nory-purple/40 leading-relaxed">Upload a stamp graphic (e.g. a cup) and an optional background. Collected stamps show full, upcoming ones appear faded.</p>
                              <div className="flex items-center gap-2">
                                <img src={`${API}/vendors/${vendor.id}/logo/stamp_image?_v=${config.stamps?._v || 0}`} alt="" onError={(e) => { e.target.style.visibility = "hidden"; }} className="w-9 h-9 rounded-lg object-contain bg-nory-purple/5 border border-nory-purple/10 shrink-0" />
                                <label className="flex-1 cursor-pointer">
                                  <span className="block text-[11px] font-semibold text-[#4f46e5]">Upload stamp image</span>
                                  <span className="block text-[9px] text-nory-purple/40">PNG with transparency recommended</span>
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStampAsset("stamp_image", e.target.files[0])} data-testid="upload-stamp-image" />
                                </label>
                              </div>
                              <div className="flex items-center gap-2">
                                <img src={`${API}/vendors/${vendor.id}/logo/stamp_bg?_v=${config.stamps?._v || 0}`} alt="" onError={(e) => { e.target.style.visibility = "hidden"; }} className="w-9 h-9 rounded-lg object-cover bg-nory-purple/5 border border-nory-purple/10 shrink-0" />
                                <label className="flex-1 cursor-pointer">
                                  <span className="block text-[11px] font-semibold text-[#4f46e5]">Upload background</span>
                                  <span className="block text-[9px] text-nory-purple/40">Optional — falls back to section background</span>
                                  <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadStampAsset("stamp_bg", e.target.files[0])} data-testid="upload-stamp-bg" />
                                </label>
                              </div>
                            </div>
                          ) : (
                            <>
                              <StampIconPicker
                                value={config.stamps?.icon || "star"}
                                onChange={(id) => set("stamps.icon", id)}
                              />
                              {[{ k: "stamped_color", l: "Filled", d: "#ffffff" }, { k: "unstamped_color", l: "Empty", d: "#555555" }].map(c => (
                                <div key={c.k} className="flex items-center gap-2">
                                  <input type="color" value={config.stamps?.[c.k] || c.d} onChange={(e) => set(`stamps.${c.k}`, e.target.value)} className="w-6 h-6 rounded border border-nory-purple/10 cursor-pointer p-0.5 shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-[10px] text-nory-purple/50">{c.l}</p>
                                    <Input value={config.stamps?.[c.k] || c.d} onChange={(e) => set(`stamps.${c.k}`, e.target.value)} className="h-6 text-[10px] font-mono" />
                                  </div>
                                </div>
                              ))}
                            </>
                          )}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  )}

                  {/* Header Fields */}
                  <AccordionItem value="header_fields">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-header-fields-trigger">Header fields ({(config.header_fields || []).length}/3)</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2">Visible when passes are stacked.</p>
                      <FieldList fields={config.header_fields || []} onChange={(v) => set("header_fields", v)} max={3} />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Primary Fields */}
                  <AccordionItem value="primary_fields">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-primary-fields-trigger">Primary fields ({(config.primary_fields || []).length}/{maxPri})</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        {ob && style === "storeCard"
                          ? "Drawn on the stamp strip (Apple’s primary band). Keep it short — e.g. member name."
                          : "Most prominent on the pass. With a strip image, Apple draws these on top of the strip."}
                      </p>
                      <FieldList fields={config.primary_fields || []} onChange={(v) => set("primary_fields", v)} max={maxPri} />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Secondary Fields */}
                  <AccordionItem value="secondary_fields">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-secondary-fields-trigger">Secondary fields ({(config.secondary_fields || []).length}/{maxSec})</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        Row below the strip. On store cards, secondary and auxiliary share this one row (Apple limit: 4 combined).
                      </p>
                      <FieldList fields={config.secondary_fields || []} onChange={(v) => set("secondary_fields", v)} max={maxSec} />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Auxiliary Fields */}
                  <AccordionItem value="auxiliary_fields">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-auxiliary-fields-trigger">Auxiliary fields ({(config.auxiliary_fields || []).length}/{maxAux})</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        On store cards these share the same row as secondary fields (not a second row).
                      </p>
                      <FieldList fields={config.auxiliary_fields || []} onChange={(v) => set("auxiliary_fields", v)} max={maxAux} />
                    </AccordionContent>
                  </AccordionItem>

                  {/* Barcode */}
                  <AccordionItem value="barcode">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-barcode-trigger">Barcode</AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        <Select value={config.barcode?.type || "QR"} onValueChange={(v) => set("barcode.type", v)}>
                          <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {["QR", "PDF417", "Aztec", "CODE128"].map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <Input value={config.barcode?.alt_text || ""} onChange={(e) => set("barcode.alt_text", e.target.value)} className="h-7 text-xs" placeholder="Alt text below barcode" />
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Lock screen locations */}
                  <AccordionItem value="locations">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-locations-trigger">
                      Lock screen locations ({(config.locations || []).length}/{MAX_LOCATIONS})
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        Apple Wallet can suggest this pass on the lock screen near these places. Up to {MAX_LOCATIONS} locations. Each can have lock screen text.
                      </p>
                      <LocationList
                        locations={config.locations || []}
                        onChange={(v) => set("locations", v)}
                      />
                      <div className="mt-3 pt-3 border-t border-nory-purple/10">
                        <p className="text-[10px] text-nory-purple/40 mb-0.5">Relevant time (optional)</p>
                        <Input
                          value={config.relevant_date || ""}
                          onChange={(e) => set("relevant_date", e.target.value)}
                          className="h-7 text-xs font-mono"
                          placeholder="2026-12-05T09:00:00Z"
                          data-testid="relevant-date"
                        />
                        <p className="text-[9px] text-nory-purple/35 mt-1 leading-relaxed">
                          ISO timestamp — Wallet may also suggest the pass around this time.
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  {/* Featured Actions */}
                  <AccordionItem value="featured_actions">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-featured-actions-trigger">
                      Featured actions ({(config.featured_actions || []).length}/{MAX_FEATURED_ACTIONS})
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        Up to two tiles beneath the Apple Wallet pass (iOS 27+). On Google Wallet the same destinations become detail links.
                      </p>
                      <FeaturedActionList
                        actions={config.featured_actions || []}
                        onChange={(v) => set("featured_actions", v)}
                      />
                    </AccordionContent>
                  </AccordionItem>

                </Accordion>
              </div>
            )}

            {tab === "design" && page === "details" && (
              <div className="flex-1 overflow-y-auto px-3">
                <Accordion type="single" collapsible defaultValue="back_fields">
                  <AccordionItem value="back_fields">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="edit-back-fields-trigger">Back fields ({(config.back_fields || []).length})</AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2">Terms, contact info, etc. Unlimited.</p>
                      <FieldList fields={config.back_fields || []} onChange={(v) => set("back_fields", v)} max={20} />
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            )}

            {tab === "notifications" && (
              <div className="flex-1 overflow-y-auto px-3">
                <Accordion type="single" collapsible defaultValue="featured_actions">
                  <AccordionItem value="featured_actions">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="notifications-featured-actions-trigger">
                      Featured actions ({(config.featured_actions || []).length}/{MAX_FEATURED_ACTIONS})
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        Apple tiles under the pass; Google maps these to links in pass details.
                      </p>
                      <FeaturedActionList
                        actions={config.featured_actions || []}
                        onChange={(v) => set("featured_actions", v)}
                      />
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="locations">
                    <AccordionTrigger className="text-xs font-semibold text-nory-purple/60 hover:no-underline py-3" data-testid="notifications-locations-trigger">
                      Lock screen locations ({(config.locations || []).length}/{MAX_LOCATIONS})
                    </AccordionTrigger>
                    <AccordionContent>
                      <p className="text-[10px] text-nory-purple/40 mb-2 leading-relaxed">
                        Suggest this pass near store locations. Customers see your lock screen text when they are close by.
                      </p>
                      <LocationList
                        locations={config.locations || []}
                        onChange={(v) => set("locations", v)}
                      />
                      <div className="mt-3 pt-3 border-t border-nory-purple/10">
                        <p className="text-[10px] text-nory-purple/40 mb-0.5">Relevant time (optional)</p>
                        <Input
                          value={config.relevant_date || ""}
                          onChange={(e) => set("relevant_date", e.target.value)}
                          className="h-7 text-xs font-mono"
                          placeholder="2026-12-05T09:00:00Z"
                          data-testid="notifications-relevant-date"
                        />
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
                <p className="text-[10px] text-nory-purple/40 px-0.5 pb-3 leading-relaxed">
                  Field change messages (updates) are still set on each field in the Design tab.
                </p>
              </div>
            )}
          </div>
        ) : (
          <button onClick={() => setRightOpen(true)} className="w-7 bg-white border-l border-nory-purple/10 flex items-center justify-center text-nory-purple/40 hover:text-nory-purple/60 shrink-0"><ChevronLeft size={14} /></button>
        )}
      </div>
    </div>
  );
}
